import type { AssayFloor, AssayItem } from '../api'

/**
 * The assay's measurements — the Admiralty scale, the floor, and time.
 *
 * ## Why the two Admiralty axes are never averaged
 *
 * Reliability is a property of the PUBLICATION; credibility is a property of the CLAIM. They
 * are ordered independently and a brief floor is a conjunction over both — `C3` means "at or
 * better than C" *and* "at or better than 3". There is no arithmetic that turns those into one
 * number without inventing a fact: an `A6` is not a strong item with one weak reading, it is a
 * claim nobody can judge that happens to have arrived from a good publication, and any mean
 * would rank it above a `C3` that a court could stand behind.
 *
 * So where this console needs one ordinate — the chronology's vertical axis — it uses the
 * BINDING axis: how far the item sits from the floor along whichever of the two is limiting
 * it. That is not a compromise between the two scales, it is the definition of the conjunction
 * they form, and it puts every item that exactly meets the floor on one line.
 */

export const RELIABILITY = ['A', 'B', 'C', 'D', 'E', 'F'] as const
export const CREDIBILITY = ['1', '2', '3', '4', '5', '6'] as const

/** The NATO/Admiralty definitions, verbatim. A grade nobody can read is a grade nobody uses. */
export const RELIABILITY_MEANING: Record<string, string> = {
  A: 'completely reliable',
  B: 'usually reliable',
  C: 'fairly reliable',
  D: 'not usually reliable',
  E: 'unreliable',
  F: 'reliability cannot be judged',
}

export const CREDIBILITY_MEANING: Record<string, string> = {
  '1': 'confirmed by other sources',
  '2': 'probably true',
  '3': 'possibly true',
  '4': 'doubtful',
  '5': 'improbable',
  '6': 'truth cannot be judged',
}

/**
 * Signed distance from the floor, in Admiralty steps, on the binding axis.
 *
 * The server computes this too and ships it on every item. This copy exists for the plate,
 * which reasons about cells rather than items — and the two must agree, because the console
 * draws the same rule twice and a reader who saw it in two places would be entitled to assume
 * they meant the same thing.
 */
export function standingOf(reliability: string, credibility: string, floor: AssayFloor): number {
  const rel = floor.reliability.charCodeAt(0) - reliability.charCodeAt(0)
  const cred = floor.credibility.charCodeAt(0) - credibility.charCodeAt(0)
  return Math.min(rel, cred)
}

export const meetsFloor = (reliability: string, credibility: string, floor: AssayFloor) =>
  standingOf(reliability, credibility, floor) >= 0

/** `B2` — the pair, always together. Neither half means anything on its own. */
export const gradeOf = (item: { reliability: string, credibility: string }) =>
  `${item.reliability}${item.credibility}`

// ── time ─────────────────────────────────────────────────────────────────────

export interface TimeDomain { from: number, to: number }

/**
 * The chronology's horizontal extent.
 *
 * ## It fits the DATA, not the window
 *
 * The window bounds custody. An item collected yesterday may describe something from 2019, so
 * the domain has to reach back past the window's left edge — that overhang is the single most
 * interesting shape this board can show, and clipping it would hide every piece of history the
 * archive has recently caught up on.
 *
 * But it is not framed BY the window either. A ten-year window over eighteen months of record
 * drew eight empty years, and a figure that is four-fifths white space has spent its resolution
 * saying something the footer already says in words. So the extent is the data's, the window's
 * own edge is drawn inside it when it happens to fall there, and the window itself is stated in
 * the colophon where a reader can check it without losing the picture.
 */
export function timeDomain(items: AssayItem[], windowDays: number): TimeDomain {
  const now = Date.now()
  let from = Number.POSITIVE_INFINITY
  let to = Number.NEGATIVE_INFINITY
  for (const item of items) {
    const collected = Date.parse(item.collectedAt)
    if (Number.isFinite(collected)) {
      if (collected > to) to = collected
      if (collected < from) from = collected
    }
    if (item.occurredAt) {
      const occurred = Date.parse(item.occurredAt)
      if (Number.isFinite(occurred) && occurred < from) from = occurred
    }
  }
  // Nothing to fit: fall back to the window, so an empty board still draws a dated axis
  // rather than a bare frame the reader cannot place.
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return { from: now - windowDays * 86_400_000, to: now }
  }
  // A degenerate domain (one item) would divide by zero in every projection below.
  if (to - from < 3_600_000) {
    from = to - 86_400_000
  }
  const pad = (to - from) * 0.03
  return { from: from - pad, to: to + pad }
}

export interface Tick { at: number, label: string, major: boolean }

/**
 * Date ticks chosen for the span, not for a fixed count.
 *
 * Round dates only — the first of a month, the first of a year. A time axis labelled at even
 * pixel intervals produces captions like "14 Mar 09:41", which a reader cannot use to place
 * anything: the whole value of a date axis is that its gridlines fall where the calendar
 * actually has edges.
 */
export function timeTicks(domain: TimeDomain): Tick[] {
  const span = domain.to - domain.from
  const day = 86_400_000
  const out: Tick[] = []
  const start = new Date(domain.from)

  if (span > day * 900) {
    for (let y = start.getFullYear(); ; y++) {
      const at = new Date(y, 0, 1).getTime()
      if (at > domain.to) break
      if (at >= domain.from) out.push({ at, label: String(y), major: true })
    }
    return out
  }
  if (span > day * 70) {
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1)
    while (cursor.getTime() <= domain.to) {
      const at = cursor.getTime()
      const january = cursor.getMonth() === 0
      if (at >= domain.from) {
        out.push({
          at,
          label: january
            ? String(cursor.getFullYear())
            : cursor.toLocaleDateString(undefined, { month: 'short' }),
          major: january,
        })
      }
      cursor.setMonth(cursor.getMonth() + 1)
    }
    return out
  }
  const step = span > day * 16 ? day * 7 : span > day * 3 ? day : 3_600_000 * 6
  const first = Math.ceil(domain.from / step) * step
  for (let at = first; at <= domain.to; at += step) {
    const d = new Date(at)
    out.push({
      at,
      label: step >= day
        ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
        : `${String(d.getHours()).padStart(2, '0')}:00`,
      major: step >= day ? d.getDate() === 1 : d.getHours() === 0,
    })
  }
  return out
}

/**
 * A stable vertical offset inside a standing band.
 *
 * Hundreds of items share one Admiralty standing, and drawn on the exact ordinate they would
 * be a single black line — which reads as one heavy fact rather than as three hundred separate
 * ones. So each item is displaced within its band by a hash of its id: deterministic, so the
 * picture is identical on every visit and an analyst can point at a dot and come back to it,
 * and never `Math.random`, which would move the evidence between renders.
 */
export function bandOffset(id: string): number {
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) / 4294967296) * 2 - 1
}

// ── latency ──────────────────────────────────────────────────────────────────

/**
 * A custody delay, in the largest honest unit.
 *
 * `null` renders as "no event date" and NEVER as zero. The whole reason this board measures
 * latency is to stop a dossier congratulating itself on speed it never had, and an undated
 * item counted as instant is exactly that flattery.
 */
export function fmtLatency(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return 'No event date'
  if (minutes < 1) return 'Under a minute'
  if (minutes < 90) return `${Math.round(minutes)} min`
  const hours = minutes / 60
  if (hours < 36) return `${Math.round(hours)} h`
  const days = hours / 24
  if (days < 60) return `${Math.round(days)} d`
  const months = days / 30.44
  if (months < 24) return `${Math.round(months)} months`
  return `${(months / 12).toFixed(1)} years`
}

/** A share, stated as a percentage of a named denominator — never a bare number. */
export const share = (part: number, whole: number) =>
  whole <= 0 ? 0 : Math.max(0, Math.min(1, part / whole))

export const pct = (value: number) => `${Math.round(value * 100)}%`
